import { CustomVideoElement } from 'custom-media-element'
import { MediaTracksMixin } from 'media-tracks'
import { CastableMediaMixin } from 'castable-video/castable-mixin'

import insightsInit from '../../utils/insights'
import HTML5EventsAdapter from '../../utils/HTML5EventsAdapter'
import setMediaMetadata from '../../utils/sessionMetadata'

const HTMLVideoMixin = (superclass) => {
  return class HTMLVideo extends superclass {
    static shadowRootOptions = { ...superclass.shadowRootOptions }

    static getTemplateHTML = (attrs) => {
      const { src, ...rest } = attrs // eslint-disable-line no-unused-vars
      let html = superclass.getTemplateHTML(rest)
      const style = html.split('</style>')
      html = style[0] + `
        video::-webkit-media-text-track-container {
          overflow: hidden;
          padding-bottom: 0;
          z-index: 0;
          text-align: center;
          color: rgba(255, 255, 255, 1);

          letter-spacing: normal;
          word-spacing: normal;
          text-transform: none;
          text-indent: 0;
          text-decoration: none;
          pointer-events: none;
          -webkit-user-select: none;
          transform: translateY(-50px) !important;

          flex: 1 1 auto;

          -webkit-line-box-contain: block inline-box replaced;
        }

        video[controls]::-webkit-media-text-track-container.visible-controls-bar {
          height: calc(100% - var(--inline-controls-bar-height) - var(--inline-controls-inside-margin));
        }

        video::cue {
          background-color: rgba(0, 0, 0, 0.1);
        }

        video::-webkit-media-text-track-display {
          position: absolute;
          overflow: hidden;
          white-space: pre-wrap;
          box-sizing: border-box;
        }

        video::-webkit-media-text-track-display-backdrop {
          display: inline-block;
          padding: 0 0.5rem;
        }

        video::cue(:future) {
          color: gray;
        }

        video::-webkit-media-text-track-container b {
          font-weight: bold;
        }

        video::-webkit-media-text-track-container u {
          text-decoration: underline;
        }

        video::-webkit-media-text-track-container i {
          font-style: italic;
        }

        video::-webkit-media-text-track-container .hidden {
          display: none;
        }

        /* Font size for mobile devices */
        @media (max-width: 600px) {
          video::cue {
            font-size: 1rem !important;
          }

          video::-webkit-media-text-track-container{
            transform: translateY(-10%) !important;
          }

        }

        /* Font size for tablets */
        @media (min-width: 601px) and (max-width: 1024px) {
          video::cue {
            font-size: 1.4rem !important;
          }
        }
        
        @media (min-width: 1025px) and (max-width: 1919px) {
          video::cue {
            font-size: 2rem !important;
          }
        }
        
        /* Font size for monitor and big desktop range between 1920 to 3000 */
        @media (min-width: 1920px) and (max-width: 3000px) {
          video::cue {
            font-size: 2.5rem !important;
          }
        }
        
        /* Additional styling for better text rendering */
        video::cue {
          text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
          text-align: center;
        }

        </style>
      `
      html += style[1]
      return html
    }

    async checkDrmSystemSupport (system) {
      return new Promise((resolve) => {
        let config
        if (system === 'com.widevine.alpha') {
          config = [{
            audioCapabilities: [{
              contentType: 'audio/mp4;codecs="mp4a.40.2"',
              encryptionScheme: 'cbcs',
              robustness: 'SW_SECURE_CRYPTO'
            }],
            videoCapabilities: [{
              contentType: 'video/mp4;codecs="avc1.42E01E"',
              encryptionScheme: 'cbcs',
              robustness: 'SW_SECURE_CRYPTO'
            }]
          }]
        } else if (system === 'com.apple.fps') {
          config = [{
            initDataTypes: ['sinf'],
            audioCapabilities: [{ contentType: 'audio/mp4;codecs="mp4a.40.2"' }],
            videoCapabilities: [{ contentType: 'video/mp4;codecs="avc1.42E01E"' }]
          }]
        } else if (system === 'com.apple.fps.1_0') {
          config = [{
            initDataTypes: ['cenc'],
            audioCapabilities: [{ contentType: 'audio/mp4;codecs="mp4a.40.2"' }],
            videoCapabilities: [{ contentType: 'video/mp4;codecs="avc1.42E01E"' }]
          }]
        } else if (system === 'com.apple.fps.2_0') {
          config = [{
            initDataTypes: ['cenc'],
            audioCapabilities: [{ contentType: 'audio/mp4;codecs="mp4a.40.2"' }],
            videoCapabilities: [{ contentType: 'video/mp4;codecs="avc1.42E01E"' }]
          }]
        } else if (system === 'com.apple.fps.3_0') {
          config = [{
            initDataTypes: ['cenc'],
            audioCapabilities: [{ contentType: 'audio/mp4;codecs="mp4a.40.2"' }],
            videoCapabilities: [{ contentType: 'video/mp4;codecs="avc1.42E01E"' }]
          }]
        }

        try {
          navigator
            .requestMediaKeySystemAccess(system, config)
            .then((mediaKeySystemAccess) => resolve(true)).catch((e) => resolve(false))
        } catch (e) {
          console.error(e)
          return resolve(false)
        }
      })
    }

    attributeChangedCallback (attrName, oldValue, newValue) {
      if (attrName !== 'src') {
        super.attributeChangedCallback(attrName, oldValue, newValue)
      }

      if (attrName === 'src' && oldValue !== newValue) {
        this.load()
      }
    }

    #destroy () {
      if (this.api) {
        this.api.detachMedia()
        this.api.destroy()
        this.api = null
      }
    }

    // Fairplay functions
    onerror (event) {
      console.error(event)
      window.console.error('A video playback error occurred')
    }

    async load () {
      this.#destroy()

      if (!this.src) {
        return
      }

      await Promise.resolve()

      setMediaMetadata(this.getAttribute('videotitle'), this.getAttribute('poster'))

      const insightSettings = this.getAttribute('insightsettings')
      const playerJSSettings = this.getAttribute('playerjssettings')
      const ctaConfig = this.getAttribute('ctadata') ? JSON.parse(this.getAttribute('ctadata')) : []
      const imageOverlayConfig = this.getAttribute('imageoverlay') ? JSON.parse(this.getAttribute('imageoverlay')) : ''

      this.nativeEl.src = this.src
      if (insightSettings) {
        insightsInit(
          JSON.parse(insightSettings),
          'html5',
          this.nativeEl,
          null,
          new Date().getTime()
        )
      }

      // this.remote.watchAvailability((available) => {
      //   const castBtn = document.getElementById('castBtn')
      //   castBtn.hidden = !available
      // })

      // Event listeners for cast debugging
      // this.remote.addEventListener('connecting', function (event) {
      //   console.log("Cast connecting", event);
      // });

      // this.remote.addEventListener('connect', function (event) {
      //   console.log("Cast connect", event);
      // });

      // this.remote.addEventListener('disconnect', function (event) {
      //   console.log("Cast disconnect", event);
      // });

      window.videoTag = this.nativeEl

      /* eslint-disable no-new */
      new HTML5EventsAdapter(
        this.nativeEl,
        JSON.parse(playerJSSettings),
        ctaConfig,
        imageOverlayConfig,
        this.getAttribute('t'),
        this.getAttribute('mu') === 'true',
        this.getAttribute('isBackgroundOnly') === 'true',
        this.getAttribute('resumewhereleft') === 'true',
        this.getAttribute('areplayercontrolsdisabled') === 'true',
        this.getAttribute('captionlanguage'),
        this.getAttribute('widgetsettings'),
        this.getAttribute('captionsoffonload') && this.getAttribute('captionsoffonload') === 'true'
      )

      await Promise.resolve()
    }
  }
}

const HTMLVideoElement = CastableMediaMixin(HTMLVideoMixin(MediaTracksMixin(CustomVideoElement)))

if (globalThis.customElements && !globalThis.customElements.get('html-video')) {
  globalThis.customElements.define('html-video', HTMLVideoElement)
}

export default HTMLVideoElement

export {
  HTMLVideoMixin,
  HTMLVideoElement
}
